import { Injectable } from '@angular/core';
import { TimezoneAwareDatePipe } from '@twaice-fe/frontend/shared/services';
import {
  CriticalSeverityColors,
  MinorSeverityColors,
  MISSING_DATA_COLOR,
  ModerateSeverityColors,
  StableSeverityColors,
} from '@twaice-fe/frontend/shared/utilities';
import { BreachHistoryEvaluation, BreachHistorySeverity, BreachHistoryWindow } from './breach-history-chart.types';

enum MissingDataSeverity {
  MissingData = 'missing_data',
}

@Injectable()
export class BreachHistoryChartService {
  stateMap: Record<BreachHistorySeverity | MissingDataSeverity, { label: string; color: string }> = {
    normal: { label: 'Normal', color: StableSeverityColors.border },
    minor: { label: 'Minor breach', color: MinorSeverityColors.accent },
    medium: { label: 'Moderate breach', color: ModerateSeverityColors.accent },
    high: { label: 'Critical breach', color: CriticalSeverityColors.accent },
    [MissingDataSeverity.MissingData]: { label: 'Missing data', color: MISSING_DATA_COLOR },
  };

  private storageTimezone: string | null = null;

  constructor(private datePipe: TimezoneAwareDatePipe) {}

  /**
   * `requestedWindow` is the window that was actually asked of the API. When given, the axis and the
   * missing-data fills anchor to it rather than to the returned data, so a trailing data gap can't
   * shift the visible window backwards. Callers that don't supply one keep the legacy behaviour.
   */
  getEvaluationsChartOptions(
    ruleEvaluations: BreachHistoryEvaluation[],
    lookbackDays: number,
    requestedWindow: BreachHistoryWindow | null = null,
    storageTimezone: string | null = null
  ) {
    this.storageTimezone = storageTimezone;
    const lookbackDaysMs = lookbackDays * 24 * 60 * 60 * 1000;
    const blocks = this.mergeContiguousStateBlocks(ruleEvaluations, lookbackDaysMs, requestedWindow);

    const presentSeverities = Array.from(new Set(blocks.map((b) => b.state))).sort();

    const legendData = presentSeverities.map((s) => ({
      name: this.stateMap[s].label,
      icon: 'circle',
    }));

    const option: echarts.EChartsOption = {
      grid: {
        left: 4,
        right: 12,
        top: 35,
        bottom: 0,
        containLabel: true,
      },
      title: {
        text: 'Breach history',
        left: 6,
        textStyle: {
          fontSize: '16px',
          fontWeight: 'normal',
          fontFamily: 'proxima-nova, sans-serif',
        },
      },
      tooltip: {
        formatter: (params: any) => this.tooltipFormatter(params),
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#444',
        },
        extraCssText: 'box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12); border-radius: 8px; padding: 8px 12px;',
      },
      legend: {
        data: legendData,
        right: 6,
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: (value: number) => {
            const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
            if (this.storageTimezone) opts.timeZone = this.storageTimezone;
            return new Intl.DateTimeFormat(undefined, opts).format(new Date(value));
          },
          align: 'left',
        },
        // Anchor to the requested window so a trailing data gap can't shift the visible window
        // backwards. Without one, fall back to looking back from the latest evaluation.
        ...(requestedWindow
          ? { min: requestedWindow.start, max: requestedWindow.end }
          : { min: (data: { max: number }) => data.max - lookbackDaysMs }),
      },
      yAxis: {
        type: 'category',
        data: [''],
        show: false,
      },
      series: presentSeverities.map((severity) => {
        const blocksForSeverity = blocks.filter((b) => b.state === severity);

        return {
          name: this.stateMap[severity].label,
          itemStyle: {
            color: this.stateMap[severity].color,
          },
          type: 'custom',
          renderItem: (params: any, api: any) => {
            const start = api.value(0);
            const end = api.value(1);
            const yIndex = 0;

            const startCoord = api.coord([start, yIndex]);
            const endCoord = api.coord([end, yIndex]);
            const height = 20;

            return {
              type: 'rect',
              shape: {
                x: startCoord[0],
                y: startCoord[1] - height / 2,
                width: endCoord[0] - startCoord[0],
                height: height,
              },
              style: {
                fill: this.stateMap[severity].color,
                stroke: this.stateMap[severity].color,
                lineWidth: 0.5,
              },
            };
          },
          encode: {
            x: [0, 1],
            y: 2,
          },
          data: blocksForSeverity.map((b) => [b.start, b.end, b.state]),
        };
      }),
    };

    return option;
  }

  private mergeContiguousStateBlocks(
    measurements: BreachHistoryEvaluation[],
    lookbackDaysMs: number,
    requestedWindow: BreachHistoryWindow | null
  ) {
    const blocks: { state: BreachHistorySeverity; start: number; end: number }[] = [];

    for (let i = 0; i < measurements.length; i++) {
      const current = measurements[i];
      const currentStart = new Date(current.start).getTime();
      const currentEnd = new Date(current.end).getTime();

      if (
        blocks.length > 0 &&
        blocks[blocks.length - 1].state === current.state &&
        blocks[blocks.length - 1].end === currentStart
      ) {
        blocks[blocks.length - 1].end = currentEnd;
      } else {
        blocks.push({
          state: current.state,
          start: currentStart,
          end: currentEnd,
        });
      }
    }

    return this.fillMissingDataGaps(blocks, lookbackDaysMs, requestedWindow);
  }

  /**
   * Fills gaps between blocks, and — when `requestedWindow` is given — leading/trailing gaps
   * against its bounds, so the chart never silently shrinks around missing data. With no window,
   * falls back to data-derived bounds and no trailing fill.
   */
  private fillMissingDataGaps(
    blocks: { state: BreachHistorySeverity | MissingDataSeverity; start: number; end: number }[],
    lookbackDaysMs: number,
    requestedWindow: BreachHistoryWindow | null
  ) {
    // Fills internal gaps into a new array while walking the sorted input, rather than mutating
    // the array being iterated.
    const sortedBlocks = blocks
      .sort((a, b) => a.start - b.start)
      .reduce<typeof blocks>((withGapsFilled, block) => {
        const previous = withGapsFilled[withGapsFilled.length - 1];
        if (previous && previous.end !== block.start) {
          withGapsFilled.push({ state: MissingDataSeverity.MissingData, start: previous.end, end: block.start });
        }
        withGapsFilled.push(block);
        return withGapsFilled;
      }, []);

    if (sortedBlocks.length === 0) {
      return sortedBlocks;
    }

    const windowStart = requestedWindow ? requestedWindow.start : sortedBlocks[sortedBlocks.length - 1].end - lookbackDaysMs;
    const windowEnd = requestedWindow ? requestedWindow.end : sortedBlocks[sortedBlocks.length - 1].end;

    // if we miss data at the beginning, fill it up
    if (sortedBlocks[0].start > windowStart) {
      sortedBlocks.unshift({
        state: MissingDataSeverity.MissingData,
        start: windowStart,
        end: sortedBlocks[0].start,
      });
    }

    const lastBlock = sortedBlocks[sortedBlocks.length - 1];
    if (lastBlock.end < windowEnd) {
      sortedBlocks.push({
        state: MissingDataSeverity.MissingData,
        start: lastBlock.end,
        end: windowEnd,
      });
    }

    return sortedBlocks;
  }

  private tooltipFormatter(params: any) {
    const start = new Date(params.value[0]);
    const end = new Date(params.value[1]);

    const format = (date: Date) => this.datePipe.transform(date, 'MMM dd, HH:mm z') ?? '';

    const durationMs = end.getTime() - start.getTime();
    const minutes = durationMs / (1000 * 60);
    const hours = minutes / 60;
    const days = hours / 24;

    let durationStr = '';
    if (minutes < 60) {
      durationStr = `${Math.round(minutes)} minutes`;
    } else if (hours < 24) {
      durationStr = `${Math.floor(hours)}h ${Math.round(minutes % 60)}m`;
    } else {
      const d = Math.floor(days);
      const h = Math.floor(hours % 24);
      const m = Math.round(minutes % 60);
      durationStr = `${d}d ${h}h ${m}m`;
    }
    return `
          <div style="font-family: 'Inter', sans-serif; font-size: 13px; line-height: 1.6;">
            <div style="font-weight: 600;">
            ${params.marker}
            <span>
              ${format(start)} - ${format(end)}
            </span>

            </div>
            <div>Duration:&nbsp;&nbsp;<strong>${durationStr}</strong></div>
          </div>
        `;
  }
}
